import {
  BUUnitData,
  OrgTreeData,
  OrganizationUnit,
} from '~/data/orgunits/types';

export enum WorkflowProcessType {
  AllowanceProposal = 'ALLOWANCE_PROPOSAL',
  BenefitProposal = 'BENEFIT_PROPOSAL',
  BillableHours = 'BILLABLE_HOURS',
  BonusProposal = 'BONUS_PROPOSAL',
  JobOpening = 'JOB_OPENING',
  SalaryProposal = 'SALARY_PROPOSAL',
  EmploymentDetailsProposal = 'EMPLOYMENT_DETAILS_PROPOSAL',
  EmploymentPositionProposal = 'POSITION_INTERNAL_PROPOSAL',
}

const workflowProcessTypeTranslationKeys: {
  [key in WorkflowProcessType]: string;
} = {
  [WorkflowProcessType.AllowanceProposal]: 'laALLOWANCE_PROPOSAL',
  [WorkflowProcessType.BenefitProposal]: 'laBENEFIT_PROPOSAL',
  [WorkflowProcessType.BillableHours]: 'laBILLABLE_HOURS',
  [WorkflowProcessType.BonusProposal]: 'laBONUS_PROPOSAL',
  [WorkflowProcessType.JobOpening]: 'laJOB_OPENING',
  [WorkflowProcessType.SalaryProposal]: 'laSALARY_PROPOSAL',
  [WorkflowProcessType.EmploymentDetailsProposal]:
    'laEMPLOYMENT_DETAILS_PROPOSAL',
  [WorkflowProcessType.EmploymentPositionProposal]:
    'laPOSITION_INTERNAL_PROPOSAL',
};

export const getWorkflowProcessTypeTranslationKey = (
  processType: WorkflowProcessType,
): string => {
  return workflowProcessTypeTranslationKeys[processType];
};

export const getWorkflowProcessTypeTranslationKeyFromString = (
  processType: WorkflowProcessType | string,
): string => {
  const isEnumValue = (Object.values(WorkflowProcessType) as string[]).includes(
    processType as string,
  );

  return isEnumValue
    ? getWorkflowProcessTypeTranslationKey(processType as WorkflowProcessType)
    : processType;
};

export enum WorkflowProcessConfigState {
  Undefined = 'UNDEFINED',
  Inherited = 'INHERITED',
  Unit = 'UNIT',
}

export const getOrgUnitLabel = (orgUnit: OrganizationUnit) =>
  `${orgUnit.fOrgUnitName} (${orgUnit.fOrgUnitNumber})`;

export enum WorkflowRoles {
  EMPLOYEE = 'EMPLOYEE',
  MANAGER = 'MANAGER',
  ALT_MANAGER = 'ALT_MANAGER',
  RESPONSIBLE_HR = 'RESPONSIBLE_HR',
  HR_ADMIN = 'HR_ADMIN',
  FIRST_APPROVER = 'FIRST_APPROVER',
  FINAL_APPROVER = 'FINAL_APPROVER',
}

const workflowRoleTranslationKeys: { [key in WorkflowRoles]: string } = {
  [WorkflowRoles.EMPLOYEE]: 'laEmployee',
  [WorkflowRoles.MANAGER]: 'laManager',
  [WorkflowRoles.ALT_MANAGER]: 'laAltManager',
  [WorkflowRoles.RESPONSIBLE_HR]: 'laHRResponsible',
  [WorkflowRoles.HR_ADMIN]: 'laHRAdmin',
  [WorkflowRoles.FIRST_APPROVER]: 'firstApprover',
  [WorkflowRoles.FINAL_APPROVER]: 'finalApprover',
};

export const getWorkflowRolesTranslationKey = (
  roleName: WorkflowRoles,
): string => {
  return workflowRoleTranslationKeys[roleName];
};

export const getWorkflowRolesTranslationKeyFromString = (
  roleName: WorkflowRoles | string,
): string => {
  const isEnumValue = (Object.values(WorkflowRoles) as string[]).includes(
    roleName as string,
  );

  return isEnumValue
    ? getWorkflowRolesTranslationKey(roleName as WorkflowRoles)
    : roleName;
};

export const getOrgTreeHeadUnits = (
  orgTrees: OrgTreeData[],
  treeId: number = 1,
): BUUnitData[] => {
  return orgTrees
    .filter((tree) => tree.fTreeId === treeId)
    .map((tree) => {
      return tree.units.filter((unit) => unit.fTreeParentUnitId === '#');
    })
    .flat();
};

export const sortBUUnitData = (
  unitData: BUUnitData[],
  sortBy?: 'unitName' | 'unitNumber',
) => {
  switch (sortBy) {
    case 'unitNumber':
      return unitData.sort((a, b) =>
        a.fTreeUnitId.localeCompare(b.fTreeUnitId),
      );
    case 'unitName':
    default:
      return unitData.sort((a, b) =>
        a.fTreeUnitName.localeCompare(b.fTreeUnitName),
      );
  }
};
